import {
  HomeIcon,
  BellIcon,
  Squares2X2Icon,
  DocumentChartBarIcon,
  ViewColumnsIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  BuildingOffice2Icon,
  // UsersIcon, // used by commented-out My Team nav item
  UserGroupIcon,
  WrenchScrewdriverIcon,
  LifebuoyIcon,
  ArrowsRightLeftIcon,
  RectangleStackIcon,
  BookOpenIcon,
  ChartBarIcon,
  InboxStackIcon,
  MapIcon,
  NewspaperIcon,
  MapPinIcon,
  ClipboardDocumentCheckIcon,
  RocketLaunchIcon,
  SignalIcon,
  ChatBubbleLeftRightIcon,
  PhoneArrowUpRightIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  AdjustmentsHorizontalIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { PERMISSIONS, ROLES } from "../../config/rolePermissions";
import { TOOL_KEYS } from "../../config/toolAccess";

export const NAV_SECTIONS = [
  {
    label: "Documents",
    items: [
      {
        name: "Documents",
        href: "/documents",
        icon: BookOpenIcon,
      },
      /**
       * The playbook and the workflow catalogue described the same delivery from
       * two ends and had two nav entries. The playbook now carries each stage's
       * procedures inline (joined on `lifecycleStage`), so it is the front door
       * and the by-department catalogue is a link inside it.
       *
       * `/workflows` and every run route stay alive — they are linked from here,
       * from the workspace and from Pending Work. Removing a nav item is not the
       * same as removing the function, and only the first is intended.
       */
      {
        name: "Playbook & Workflows",
        href: "/playbook",
        icon: MapIcon,
      },
      {
        name: "News",
        href: "/news",
        icon: NewspaperIcon,
      },
    ],
  },
  {
    label: "Live",
    items: [
      {
        name: "Live",
        href: "/live",
        icon: SignalIcon,
        liveDot: true,
        roles: [ROLES.ADMIN, ROLES.MANAGER],
      },
      {
        name: "Queries",
        href: "/live/queries",
        icon: ChatBubbleLeftRightIcon,
        roles: [ROLES.ADMIN, ROLES.MANAGER],
      },
      {
        name: "Emails",
        href: "/live/emails",
        icon: EnvelopeIcon,
        roles: [ROLES.ADMIN, ROLES.MANAGER],
      },
    ],
  },
  {
    label: "Workspace",
    items: [
      {
        name: "My Workspace",
        href: '/workspace',
        icon: HomeIcon,
        roles: [ROLES.ADMIN],
      },
      {
        name: "Dashboard",
        href: "/dashboard",
        icon: Squares2X2Icon,
        permission: PERMISSIONS.VIEW_GLOBAL_DASHBOARD,
      },
      {
        name: "Practices / Locations",
        href: "/clients",
        icon: MapPinIcon,
        // Either permission: the whole book (VIEW_CLIENTS) or a team member's own
        // department, read-only (VIEW_OWN_CLIENTS). A readiness "fix this" link must
        // never point somewhere the menu says does not exist.
        permission: [PERMISSIONS.VIEW_CLIENTS, PERMISSIONS.VIEW_OWN_CLIENTS],
      },
      {
        name: "Client Onboarding",
        href: "/client-onboarding",
        icon: RocketLaunchIcon,
        toolKey: TOOL_KEYS.CLIENT_ONBOARDING,
      },
      {
        name: "My Onboarding Queue",
        href: "/client-onboarding/my-queue",
        icon: ClipboardDocumentListIcon,
        toolKey: TOOL_KEYS.CLIENT_ONBOARDING,
      },
      {
        name: "Pending Work",
        href: "/pending-work",
        icon: ClipboardDocumentListIcon,
        toolKey: TOOL_KEYS.PENDING_WORK,
      },
      /**
       * No `roles`, `permission` or `toolKey`: the queue is already scoped to the
       * locations the caller can see, and the right to DECIDE is being named on
       * the approval itself. Gating the menu entry on a permission would hide it
       * from the team leads who are the approvers.
       */
      {
        name: "Approvals",
        href: "/approvals",
        icon: ShieldCheckIcon,
      },
      // "Workflows" is no longer its own entry — see "Playbook & Workflows"
      // above. The route is still live and still linked from the playbook.
      /**
       * The primary ingestion for the active/inactive client workflow. Sits
       * beside Practices / Locations because it answers the other half of the
       * same question: that page is who the client is, this one is what they buy.
       */
      {
        /**
         * VISIBLE TO EVERYONE, WRITABLE BY ADMINS.
         *
         * "Does this client have SEO" is asked by every department before starting
         * work, and the answer gates their own queue — so the read is not something
         * to hold behind a client permission. The write is `MANAGE_CLIENTS` on the
         * server and the page itself says so in a banner, which is a better way to
         * express "look, don't touch" than hiding the answer.
         *
         * No `permission` key at all, deliberately: the sidebar's check has no admin
         * bypass, so listing one here is how a role ends up unable to see a page it
         * is allowed to read.
         */
        name: "Service Plan",
        href: "/service-plan",
        icon: AdjustmentsHorizontalIcon,
      },
      /**
       * VISIBLE TO EVERYONE, WRITABLE BY THE SAME GUARD AS THE TARGETS UPLOAD.
       *
       * Same reasoning as Service Plan: hiding the page behind a permission is
       * how a role that may read the answer cannot reach the screen that shows
       * it. The write is gated on the server; the page itself says so.
       */
      {
        name: "Monthly Budgets",
        href: "/monthly-budgets",
        icon: CurrencyDollarIcon,
      },
      {
        name: "Readiness",
        href: "/clients/readiness",
        icon: ClipboardDocumentCheckIcon,
        permission: PERMISSIONS.VIEW_CLIENTS,
      },
      {
        name: "Reports",
        href: "/reports",
        icon: DocumentChartBarIcon,
        permission: PERMISSIONS.VIEW_REPORTS,
      },
    ],
  },
  {
    label: "Execution",
    items: [
      {
        name: "Stages",
        href: "/stages",
        icon: ArrowsRightLeftIcon,
        permission: PERMISSIONS.MANAGE_SETTINGS,
      },
      /**
       * Department Queue's nav entry is removed — the workspace queue covers the
       * same ground and had the same rows. The ROUTE stays alive on purpose:
       * `/assignments/queue` is still linked from elsewhere, and removing a nav
       * item while its function is unavailable is worse than the duplication.
       */
      {
        name: "Kanban",
        href: "/kanban",
        icon: ViewColumnsIcon,
        permission: [PERMISSIONS.VIEW_ALL_TASKS, PERMISSIONS.VIEW_OWN_TASKS],
      },
      {
        name: "Task List",
        href: "/tasks",
        icon: ClipboardDocumentListIcon,
        permission: [PERMISSIONS.VIEW_ALL_TASKS, PERMISSIONS.VIEW_OWN_TASKS],
      },
      {
        name: "My Activity",
        href: "/my-activity",
        icon: CalendarDaysIcon,
        roles: [ROLES.ADMIN, ROLES.MANAGER],
      },
    ],
  },
  {
    label: "Report Card",
    items: [
      {
        name: "Report Card",
        href: "/report-card",
        icon: AcademicCapIcon,
        permission: PERMISSIONS.VIEW_REPORT_CARD,
      },
    ],
  },
  {
    label: "Tools",
    items: [
      {
        name: "Tools",
        href: "/tools",
        icon: WrenchScrewdriverIcon,
        toolsHub: true,
      },
      {
        name: "Call Centre",
        href: "/call-centre",
        icon: PhoneArrowUpRightIcon,
        // Tool key only — the same single gate the route uses, so the menu can
        // never offer a page the route refuses (nav-route-drift.test.js).
        toolKey: TOOL_KEYS.CONTACT_CENTER_INTEL,
      },
      {
        name: "Tool catalog",
        href: "/tool-catalog",
        icon: RectangleStackIcon,
        toolKey: TOOL_KEYS.TOOL_CATALOG,
      },
    ],
  },
  {
    label: "Manage",
    items: [
      {
        // Anyone can raise one, so this is deliberately not role-restricted.
        name: "Requests",
        href: "/requests",
        icon: InboxStackIcon,
      },
      {
        name: "Content generation",
        href: "/content-generation-requests",
        icon: InboxStackIcon,
        permission: PERMISSIONS.MANAGE_CONTENT_REQUESTS,
        pendingBadge: true,
      },
      {
        name: "Activity Analysis",
        href: "/activity-analysis",
        icon: ChartBarIcon,
        permission: PERMISSIONS.VIEW_ACTIVITY,
      },
      {
        name: "AI Usage",
        href: "/ai-usage",
        icon: CpuChipIcon,
        permission: PERMISSIONS.VIEW_AI_USAGE,
      },
      {
        name: "Tool Run Logs",
        href: "/tool-runs",
        icon: ClipboardDocumentListIcon,
        permission: PERMISSIONS.VIEW_AUDIT_LOG,
      },
      {
        // Departments → teams → clients. Visible to anyone who can see People;
        // changing a team needs MANAGE_TEAMS, enforced on the route and the API.
        name: "Teams",
        href: "/teams",
        icon: UserGroupIcon,
        permission: [PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_TEAMS],
      },
      {
        // The access-model rollout console. Admin only: it names who would lose
        // access to what, which is not a list its subjects should browse.
        name: "Access Rollout",
        href: "/access",
        icon: ShieldCheckIcon,
        permission: PERMISSIONS.MANAGE_ACCESS_CONTROL,
      },
      // {
      //   name: "My Team",
      //   href: "/my-team",
      //   icon: UsersIcon,
      //   roles: [ROLES.MANAGER],
      //   // Narrower than the route (which admits admin via MANAGE_USERS) on
      //   // purpose: the page is scoped to the viewer's own department, and an
      //   // admin has none. Typing the URL still works; the menu just does not
      //   // offer a page that would render as "your department" with no department.
      //   hideFromAdmin: true,
      // },
      // One menu entry: agents go to the console; everyone else raises tickets at /support.
      {
        name: "Support",
        href: "/support",
        icon: LifebuoyIcon,
        hideIfTool: TOOL_KEYS.SUPPORT_CENTER,
      },
      {
        name: "Support",
        href: "/support-center",
        icon: InboxStackIcon,
        toolKey: TOOL_KEYS.SUPPORT_CENTER,
      },
      {
        name: "Notifications",
        href: "/inbox",
        icon: BellIcon,
      },
      {
        name: "Client mail",
        href: "/client-mail",
        icon: EnvelopeOpenIcon,
        // Admins and the DPM coordinator department (main or second) — not a role, so the server decides.
        clientMail: true,
      },
      {
        name: "Settings",
        href: "/settings",
        icon: Cog6ToothIcon,
      },
    ],
  },
];

// Flat list for backwards compatibility
export const NAV_ITEMS = NAV_SECTIONS.flatMap((s) => s.items);
